import { InsiderTradingPlansComponent } from "./components/insider-trading-plans/insider-trading-plans.component";
import { IssuerDetailsComponent } from "./components/issuer-details/issuer-details.component";
import { InsiderDetailsComponent } from "./components/insider-details/insider-details.component";

import { forwardRef, InjectionToken, NgModule, Provider } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { providePrimeNG } from "primeng/config";
import Aura from "@primeng/themes/aura";
import { ButtonModule } from "primeng/button";
import { ToolbarModule } from "primeng/toolbar"; // Optional for navbar styling
import { SidebarModule } from "primeng/sidebar";
import { PanelModule } from "primeng/panel";
import { MappingComponent } from "./components/mapping/mapping.component";
import { CardModule } from "primeng/card";

import { TableModule } from "primeng/table";
import { DataViewComponent } from "./components/data-view/data-view.component";
import { ProductService } from "src/core/services/product.service";
import { QualityGateComponent } from "./components/quality-gate/quality-gate.component";
import { MenubarModule } from "primeng/menubar";
import { AvatarModule } from "primeng/avatar";
import { BadgeModule } from "primeng/badge";
import { InputTextModule } from "primeng/inputtext";
import { PanelMenuModule } from "primeng/panelmenu";
import { InboxComponent } from "./components/inbox/inbox.component";
import { TabsModule } from "primeng/tabs";
import { DialogModule } from "primeng/dialog";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TooltipModule } from "primeng/tooltip";
import { DatePickerModule } from "primeng/datepicker";
import {
  DialogService,
  DynamicDialogModule,
  DynamicDialogRef,
} from "primeng/dynamicdialog";

import { LlmComponent } from "./components/inbox/llm/llm.component";
import { ProcessedComponent } from "./components/inbox/processed/processed.component";
import { ManualComponent } from "./components/inbox/manual/manual.component";
import { AddDataComponent } from "./components/inbox/modals/add-data/add-data.component";
import { EditDataComponent } from "./components/inbox/modals/edit-data/edit-data.component";
import { ViewDataComponent } from "./components/inbox/modals/view-data/view-data.component";
import { MapDataComponent } from "./components/mapping/modals/map-data/map-data.component";
import { IssuerComponent } from "./components/mapping/issuer/issuer.component";
import { InsiderComponent } from "./components/mapping/insider/insider.component";
import { EditMapDataComponent } from "./components/mapping/modals/edit-data/edit-data.component";
import { ViewMapDataComponent } from "./components/mapping/modals/view-data/view-data.component";
import { EditInsiderDataComponent } from "./components/mapping/modals/edit-insider-data/edit-insider-data.component";
import { MapInsiderDataComponent } from "./components/mapping/modals/map-insider-data/map-insider-data.component";
import { ViewInsiderDataComponent } from "./components/mapping/modals/view-insider-data/view-insider-data.component";
import { SecurityBreakdownComponent } from "./components/quality-gate/security-breakdown/security-breakdown.component";
import { AddFormComponent } from "./components/quality-gate/modals/add-form/add-form.component";
import { LoginComponent } from "./components/login/login.component";
import { AuthenticationService } from "src/core/services/authentication.service";
import { environment } from "src/environments/environment";
import { ExceptionDiscardComponent } from "./components/exception-handling/discard/discard.component";
import {
  OAuthModule,
  OAuthService,
  UrlHelperService,
} from "angular-oauth2-oidc";
import { ApiModule } from "src/api/api.module";
import { ConfirmationService, MessageService } from "primeng/api";
import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { ApiRequestConfiguration } from "src/interceptors/api.request.configuration";
import { ApiInterceptor } from "src/interceptors/api.interceptor";
import { ConfirmDialog } from "primeng/confirmdialog";
import { Toast } from "primeng/toast";
import { ProcessedViewComponent } from "./components/inbox/modals/processed-view/processed-view.component";
import { Checkbox } from "primeng/checkbox";
import { ProcessedEditComponent } from "./components/inbox/modals/processed-edit/processed-edit.component";
import { DiscardComponent } from "./components/inbox/discard/discard.component";
import { DiscardConfirmationComponent } from "./components/inbox/modals/discard-confirmation/discard-confirmation.component";
import { ExceptionHandlingComponent } from "./components/exception-handling/exception-handling.component";
import { UpdateExtractionComponent } from "./components/exception-handling/update-extraction/update-extraction.component";
import { DiscardConfirmationExceptionHandlingComponent } from "./components/exception-handling/discard-confirmation-exception-handling/discard-confirmation-exception-handling.component";
import { SecurityComponent } from "./components/mapping/security/security.component";
import { IssuerProcessedComponent } from "./components/mapping/issuer/processed/processed.component";
import { IssuerUnprocessedComponent } from "./components/mapping/issuer/unprocessed/unprocessed.component";
import { IssuerUnreferencedComponent } from "./components/mapping/issuer/unreferenced/unreferenced.component";
import { InsiderProcessedComponent } from "./components/mapping/insider/processed/processed.component";
import { InsiderUnprocessedComponent } from "./components/mapping/insider/unprocessed/unprocessed.component";
import { InsiderUnreferencedComponent } from "./components/mapping/insider/unreferenced/unreferenced.component";
import { SecurityProcessedComponent } from "./components/mapping/security/processed/processed.component";
import { SecurityUnprocessedComponent } from "./components/mapping/security/unprocessed/unprocessed.component";
import { SecurityUnreferencedComponent } from "./components/mapping/security/unreferenced/unreferenced.component";
import { DropdownModule } from "primeng/dropdown";
import { EditSecurityDataComponent } from "./components/mapping/modals/edit-security-data/edit-security-data.component";
import { MapSecurityDataComponent } from "./components/mapping/modals/map-security-data/map-security-data.component";
import { ViewSecurityDataComponent } from "./components/mapping/modals/view-security-data/view-security-data.component";
import { AggregateSharesValidationComponent } from "./components/quality-gate/aggregate-shares-validation/aggregate-shares-validation.component";
import { MissingSecurityBreakdownComponent } from "./components/quality-gate/missing-security-breakdown/missing-security-breakdown.component";
import { MissingSharesInfoComponent } from "./components/quality-gate/missing-shares-info/missing-shares-info.component";
import { SecurityTypeFlagsComponent } from "./components/quality-gate/security-type-flags/security-type-flags.component";
import { TransactionTypeBuyComponent } from "./components/quality-gate/transaction-type-buy/transaction-type-buy.component";
import { TransactionTypeOtherComponent } from "./components/quality-gate/transaction-type-other/transaction-type-other.component";
import { MissingSharesProcessedComponent } from "./components/quality-gate/missing-shares-info/processed/processed.component";
import { MissingSharesUnprocessedComponent } from "./components/quality-gate/missing-shares-info/unprocessed/unprocessed.component";
import { TypeFlagsProcessedComponent } from "./components/quality-gate/security-type-flags/processed/processed.component";
import { TypeFlagsUnprocessedComponent } from "./components/quality-gate/security-type-flags/unprocessed/unprocessed.component";
import { TypeOtherProcessedComponent } from "./components/quality-gate/transaction-type-other/processed/processed.component";
import { TypeOtherUnprocessedComponent } from "./components/quality-gate/transaction-type-other/unprocessed/unprocessed.component";
import { TypeBuyUnprocessedComponent } from "./components/quality-gate/transaction-type-buy/unprocessed/unprocessed.component";
import { TypeBuyProcessedComponent } from "./components/quality-gate/transaction-type-buy/processed/processed.component";
import { AggregateSharesProcessedComponent } from "./components/quality-gate/aggregate-shares-validation/processed/processed.component";
import { AggregateSharesUnprocessedComponent } from "./components/quality-gate/aggregate-shares-validation/unprocessed/unprocessed.component";
import { MissingSecurityUnprocessedComponent } from "./components/quality-gate/missing-security-breakdown/unprocessed/unprocessed.component";
import { MissingSecurityProcessedComponent } from "./components/quality-gate/missing-security-breakdown/processed/processed.component";
import { MultiSelectModule } from "primeng/multiselect";
import { AddSecurityTagComponent } from "./components/inbox/modals/add-security-tag/add-security-tag.component";
import { EditSecurityTagComponent } from "./components/inbox/modals/edit-security-tag/edit-security-tag.component";
import { ViewSecurityTagComponent } from "./components/inbox/modals/view-security-tag/view-security-tag.component";
import { DuplicateFilingsComponent } from "./components/quality-gate/duplicate-filings/duplicate-filings.component";
import { DuplicateProcessedComponent } from "./components/quality-gate/duplicate-filings/processed/processed.component";
import { DuplicateUnprocessedComponent } from "./components/quality-gate/duplicate-filings/unprocessed/unprocessed.component";
import { ViewFilingsComponent } from "./components/quality-gate/modals/view-filings/view-filings.component";
import { InsiderRelationshipComponent } from "./components/mapping/insider-relationship/insider-relationship.component";
import { InsiderRelationshipProcessedComponent } from "./components/mapping/insider-relationship/processed/processed.component";
import { InsiderRelationshipUnprocessedComponent } from "./components/mapping/insider-relationship/unprocessed/unprocessed.component";
import { UpdateInsiderRelationshipComponent } from "./components/mapping/modals/update-insider-relationship/update-insider-relationship.component";
import { RemapInsiderRelationshipComponent } from "./components/mapping/modals/remap-insider-relationship/remap-insider-relationship.component";
import { CheckPlanActionComponent } from "./components/quality-gate/check-plan-action/check-plan-action.component";
import { CheckPlanUnprocessedComponent } from "./components/quality-gate/check-plan-action/unprocessed/unprocessed.component";
import { CheckPlanProcessedComponent } from "./components/quality-gate/check-plan-action/processed/processed.component";
import { MissingDateAdoptionComponent } from "./components/quality-gate/missing-date-adoption/missing-date-adoption.component";
import { MissingAdoptionProcessedComponent } from "./components/quality-gate/missing-date-adoption/processed/processed.component";
import { MissingAdoptionUnprocessedComponent } from "./components/quality-gate/missing-date-adoption/unprocessed/unprocessed.component";
import { NullSecurityTypeComponent } from "./components/quality-gate/null-security-type/null-security-type.component";
import { NullTransactionTypeComponent } from "./components/quality-gate/null-transaction-type/null-transaction-type.component";
import { NullSecurityProcessedComponent } from "./components/quality-gate/null-security-type/processed/processed.component";
import { NullSecurityUnprocessedComponent } from "./components/quality-gate/null-security-type/unprocessed/unprocessed.component";
import { NullTransactionProcessedComponent } from "./components/quality-gate/null-transaction-type/processed/processed.component";
import { NullTransactionUnprocessedComponent } from "./components/quality-gate/null-transaction-type/unprocessed/unprocessed.component";
import { NonIndividualRelationshipComponent } from "./components/quality-gate/non-individual-relationship/non-individual-relationship.component";
import { NonIndividualRelationshipUnprocessedComponent } from "./components/quality-gate/non-individual-relationship/unprocessed/unprocessed.component";
import { NonIndividualRelationshipProcessedComponent } from "./components/quality-gate/non-individual-relationship/processed/processed.component";
import { AdoptionDateAmendmentDateComponent } from "./components/quality-gate/adoption-date-amendment-date/adoption-date-amendment-date.component";
import { EffectiveDateAdoptionDateComponent } from "./components/quality-gate/effective-date-adoption-date/effective-date-adoption-date.component";
import { ExpirationDateAdoptionDateComponent } from "./components/quality-gate/expiration-date-adoption-date/expiration-date-adoption-date.component";
import { ExpirationDateDurationDaysNullComponent } from "./components/quality-gate/expiration-date-duration-days-null/expiration-date-duration-days-null.component";
import { ExpirationDateTerminationDateComponent } from "./components/quality-gate/expiration-date-termination-date/expiration-date-termination-date.component";
import { InsiderNullComponent } from "./components/quality-gate/insider-null/insider-null.component";
import { TerminationDateAdoptionDateComponent } from "./components/quality-gate/termination-date-adoption-date/termination-date-adoption-date.component";
import { UnprocessedEffectiveDateComponent } from "./components/quality-gate/effective-date-adoption-date/unprocessed/unprocessed.component";
import { UnprocessedExpirationDateAdoptionDateComponent } from "./components/quality-gate/expiration-date-adoption-date/unprocessed/unprocessed.component";
import { UnprocessedTerminationDateAdoptionDateComponent } from "./components/quality-gate/termination-date-adoption-date/unprocessed/unprocessed.component";
import { UnprocessedExpirationDateTerminationDateComponent } from "./components/quality-gate/expiration-date-termination-date/unprocessed/unprocessed.component";
import { UnprocessedAdoptionDateAmendmentDateComponent } from "./components/quality-gate/adoption-date-amendment-date/unprocessed/unprocessed.component";
import { UnprocessedExpirationDateDurationDaysNullComponent } from "./components/quality-gate/expiration-date-duration-days-null/unprocessed/unprocessed.component";
import { ProcessedExpirationDateDurationDaysNullComponent } from "./components/quality-gate/expiration-date-duration-days-null/processed/processed.component";
import { InsiderNullProcessedComponent } from "./components/quality-gate/insider-null/processed/processed.component";
import { InsiderNullUnprocessedComponent } from "./components/quality-gate/insider-null/unprocessed/unprocessed.component";
import { AddTextComponent } from "./components/inbox/modals/add-text/add-text.component";
import { DuplicatePlansComponent } from "./components/quality-gate/duplicate-plans/duplicate-plans.component";
import { DuplicatePlansProcessedComponent } from "./components/quality-gate/duplicate-plans/processed/processed.component";
import { DuplicatePlansUnprocessedComponent } from "./components/quality-gate/duplicate-plans/unprocessed/unprocessed.component";
import { ViewDuplicatePlansFilingsComponent } from "./components/quality-gate/modals/view-duplicate-plans-filings/view-duplicate-plans-filings.component";
import { CalendarModule } from "primeng/calendar";
import { InfiniteScrollDirective } from "ngx-infinite-scroll";
import { PaginatorModule } from "primeng/paginator";
import {DuplicateByCikComponent} from "./components/mapping/issuer/duplicate-by-cik/duplicate-by-cik.component";
import {
  MoreThanOneChildComponent
} from "./components/mapping/insider-relationship/more-than-one-child/more-than-one-child.component";
import {PlanLinkageComponent} from "./components/quality-gate/plan-linkage/plan-linkage.component";
import {
  PlanLinkageUnprocessedComponent
} from "./components/quality-gate/plan-linkage/plan-linkage-unprocessed/plan-linkage-unprocessed.component";
import {
  PlanLinkageProcessedComponent
} from "./components/quality-gate/plan-linkage/plan-linkage-processed/plan-linkage-processed.component";
import {ViewPlanLinkageComponent} from "./components/quality-gate/modals/view-plan-linkage/view-plan-linkage.component";
import {
  InsiderDuplicateByCikComponent
} from "./components/mapping/insider/insider-duplicate-by-cik/insider-duplicate-by-cik.component";
import {
  ViewTradingPlansComponent
} from "./components/quality-gate/modals/view-trading-plans/view-trading-plans.component";
import {
  MultipleSecurityTypesComponent
} from "./components/mapping/security/multiple-security-types/multiple-security-types.component";
import { AddInsiderCommentComponent } from "./components/mapping/modals/add-insider-comment/add-insider-comment.component";

//  import { InfiniteScrollModule } from 'ngx-infinite-scroll';

export const API_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiInterceptor),
  multi: true,
};
export const OAUTH_PROVIDER_URL = new InjectionToken<string>(
  "oauthProviderUrl"
);

@NgModule({
  declarations: [
    AppComponent,
    DataViewComponent,
    MappingComponent,
    QualityGateComponent,
    InboxComponent,
    LlmComponent,
    ProcessedComponent,
    ManualComponent,
    AddDataComponent,
    EditDataComponent,
    ViewDataComponent,
    MapDataComponent,
    IssuerComponent,
    InsiderComponent,
    SecurityComponent,
    EditMapDataComponent,
    ViewMapDataComponent,
    EditInsiderDataComponent,
    MapInsiderDataComponent,
    ViewInsiderDataComponent,
    SecurityBreakdownComponent,
    AddFormComponent,
    LoginComponent,
    ProcessedViewComponent,
    ProcessedEditComponent,
    DiscardComponent,
    DiscardConfirmationComponent,
    DiscardConfirmationExceptionHandlingComponent,
    ExceptionHandlingComponent,
    ExceptionDiscardComponent,
    IssuerProcessedComponent,
    IssuerUnprocessedComponent,
    IssuerUnreferencedComponent,
    InsiderProcessedComponent,
    InsiderUnprocessedComponent,
    InsiderUnreferencedComponent,
    SecurityProcessedComponent,
    SecurityUnprocessedComponent,
    SecurityUnreferencedComponent,
    EditSecurityDataComponent,
    MapSecurityDataComponent,
    ViewSecurityDataComponent,
    AggregateSharesValidationComponent,
    MissingSecurityBreakdownComponent,
    MissingSharesInfoComponent,
    SecurityTypeFlagsComponent,
    TransactionTypeBuyComponent,
    TransactionTypeOtherComponent,
    MissingSharesProcessedComponent,
    MissingSharesUnprocessedComponent,
    TypeFlagsProcessedComponent,
    TypeFlagsUnprocessedComponent,
    TypeOtherProcessedComponent,
    TypeOtherUnprocessedComponent,
    TypeBuyUnprocessedComponent,
    TypeBuyProcessedComponent,
    MissingSecurityUnprocessedComponent,
    MissingSecurityProcessedComponent,
    AggregateSharesProcessedComponent,
    AggregateSharesUnprocessedComponent,
    AddSecurityTagComponent,
    EditSecurityTagComponent,
    ViewSecurityTagComponent,
    DuplicateFilingsComponent,
    DuplicateProcessedComponent,
    DuplicateUnprocessedComponent,
    ViewFilingsComponent,
    InsiderRelationshipComponent,
    InsiderRelationshipProcessedComponent,
    InsiderRelationshipUnprocessedComponent,
    UpdateInsiderRelationshipComponent,
    RemapInsiderRelationshipComponent,

    CheckPlanActionComponent,
    CheckPlanProcessedComponent,
    CheckPlanUnprocessedComponent,
    MissingDateAdoptionComponent,
    MissingAdoptionProcessedComponent,
    MissingAdoptionUnprocessedComponent,
    NullSecurityTypeComponent,
    NullSecurityProcessedComponent,
    NullSecurityUnprocessedComponent,
    NullTransactionTypeComponent,
    NullTransactionProcessedComponent,
    NullTransactionUnprocessedComponent,
    NonIndividualRelationshipComponent,
    NonIndividualRelationshipUnprocessedComponent,
    NonIndividualRelationshipProcessedComponent,

    AdoptionDateAmendmentDateComponent,
    EffectiveDateAdoptionDateComponent,
    ExpirationDateAdoptionDateComponent,
    ExpirationDateDurationDaysNullComponent,
    ExpirationDateTerminationDateComponent,
    InsiderNullComponent,
    InsiderNullUnprocessedComponent,
    InsiderNullProcessedComponent,
    TerminationDateAdoptionDateComponent,
    UnprocessedEffectiveDateComponent,
    UnprocessedExpirationDateAdoptionDateComponent,
    UnprocessedTerminationDateAdoptionDateComponent,
    UnprocessedExpirationDateTerminationDateComponent,
    UnprocessedAdoptionDateAmendmentDateComponent,
    UnprocessedExpirationDateDurationDaysNullComponent,
    ProcessedExpirationDateDurationDaysNullComponent,
    AddTextComponent,
    DuplicatePlansComponent,
    DuplicatePlansProcessedComponent,
    DuplicatePlansUnprocessedComponent,
    ViewDuplicatePlansFilingsComponent,
    InsiderTradingPlansComponent,
    IssuerDetailsComponent,
    InsiderDetailsComponent,
    DuplicateByCikComponent,
    MoreThanOneChildComponent,
    DuplicateByCikComponent,
    PlanLinkageComponent,
    PlanLinkageUnprocessedComponent,
    PlanLinkageProcessedComponent,
    ViewPlanLinkageComponent,
    InsiderDuplicateByCikComponent,
    ViewTradingPlansComponent,
    MultipleSecurityTypesComponent,
    AddInsiderCommentComponent
  ],
  imports: [
    PaginatorModule,
    BrowserModule,
    AppRoutingModule,
    ButtonModule,
    SidebarModule,
    ToolbarModule,
    PanelModule,
    TableModule,
    MenubarModule,
    AvatarModule,
    BadgeModule,
    InputTextModule,
    PanelMenuModule,
    TabsModule,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    TooltipModule,
    DynamicDialogModule,
    OAuthModule.forRoot(),
    ApiModule.forRoot({rootUrl: environment.apiUrl}),
    CommonModule,
    ConfirmDialog,
    Toast,
    Checkbox,
    UpdateExtractionComponent,
    DropdownModule,
    DatePickerModule,
    MultiSelectModule,
    CardModule,
    CalendarModule,
    InfiniteScrollDirective
  ],
  providers: [
    OAuthService,
    AuthenticationService,
    UrlHelperService,
    ApiInterceptor,
    API_INTERCEPTOR_PROVIDER,
    { provide: OAUTH_PROVIDER_URL, useValue: environment.oauthProviderUrl },
    ApiRequestConfiguration,
    ProductService,
    DialogService,
    MessageService,
    ConfirmationService,
    DynamicDialogRef,
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false || "none",
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
